import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../api/auth';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Success state ────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="container-custom py-12">
        <div className="max-w-md mx-auto mt-12">
          <div className="card text-center">
            {/* Mail icon */}
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100">
              <svg
                className="w-8 h-8 text-emerald-600"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-bold mb-3 text-gray-900">Check your inbox</h1>
            <p className="text-gray-600 text-sm leading-relaxed mb-1">
              If <span className="font-semibold text-gray-800">{email}</span> is associated
              with a VMC Reviews account, you'll receive a password reset link within a few
              minutes.
            </p>
            <p className="text-gray-500 text-xs mb-6">
              The link expires in <strong>1 hour</strong>. Check your spam folder if you
              don't see the email.
            </p>

            <Link to="/login" className="btn btn-primary w-full py-2 block text-center">
              Back to Login
            </Link>

            <p className="mt-4 text-sm text-gray-500">
              Didn't get it?{' '}
              <button
                type="button"
                onClick={() => { setSubmitted(false); setEmail(''); }}
                className="text-sky-600 hover:text-sky-700 underline underline-offset-2"
              >
                Try a different email
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Request form ─────────────────────────────────────────────────────────────
  return (
    <div className="container-custom py-12">
      <div className="max-w-md mx-auto mt-12">
        <div className="card">
          {/* Lock icon */}
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-5 rounded-full bg-sky-100">
            <svg
              className="w-7 h-7 text-sky-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          <h1 className="text-2xl font-bold mb-2 text-center">Forgot your password?</h1>
          <p className="text-gray-600 text-sm text-center mb-6">
            Enter your account email and we'll send you a link to reset your password.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full py-2 mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Sending reset link…
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sky-600 hover:text-sky-700 text-sm">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
